import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ChakraAccordion,
  ChakraAccordionButton,
  ChakraAccordionIcon,
  ChakraAccordionItem,
  ChakraAccordionPanel,
  ChakraBox,
  ChakraFlex,
  Counter,
  Tag,
  TagColor,
  Text,
} from '@equip.health/ui';
import AppointmentCard from '~/components/common/calendar/AppointmentCard';
import SkeletonLoaderList from '~/components/common/SkeletonLoaderList';
import { formatShortDate, formatAppointmentStartEndTime } from '../utils';
import { DateTime } from 'luxon';
import {
  groupAppointmentsByWeek,
  mergeSelectionsIntoWeeks,
  AppointmentsPanelState,
} from './utils';

const TOP_LEVEL_ID = 'six-week-appointments-panel';


interface AppointmentsPanelProps {
  appointments: AppointmentDetailResponse[];
  focusWeekIndex?: number;
  isLoading: boolean;
  patientTimezone?: string;
  weekBuckets: WeekBucket[];
  selectionState: AppointmentsPanelState;
}

const AppointmentsPanel = ({
  appointments,
  focusWeekIndex,
  isLoading,
  patientTimezone,
  weekBuckets,
  selectionState,
}: AppointmentsPanelProps) => {
  const formatDate = useCallback(
    (iso: string) => formatShortDate(iso, patientTimezone),
    [patientTimezone],
  );

  const formatStartEndTime = useCallback(
    (start: string, end: string) => formatAppointmentStartEndTime(start, end, { timezone: patientTimezone }),
    [patientTimezone],
  );

  const groupedByWeek = useMemo(
    () => groupAppointmentsByWeek(appointments, weekBuckets),
    [appointments, weekBuckets],
  );

  const appointmentsByWeek = useMemo(
    () => mergeSelectionsIntoWeeks(groupedByWeek, weekBuckets, selectionState),
    [groupedByWeek, weekBuckets, selectionState],
  );

  const [openIndex, setOpenIndex] = useState<number | undefined>(undefined);

  const intakeWeekIndex = useMemo<number | undefined>(() => {
    if (!selectionState.intakeAppointment) {
      return undefined;
    }
    const intakeStart = DateTime.fromISO(selectionState.intakeAppointment.startTime);
    const intakeIdx = weekBuckets.findIndex(
      (bucket) => intakeStart >= bucket.start && intakeStart <= bucket.end,
    );
    return intakeIdx === -1 ? undefined : intakeIdx;
  }, [selectionState.intakeAppointment, weekBuckets]);

  useEffect(() => {
    if (intakeWeekIndex !== undefined) {
      setOpenIndex(intakeWeekIndex);
    }
  }, [intakeWeekIndex]);

  useEffect(() => {
    if (focusWeekIndex !== undefined) {
      setOpenIndex(focusWeekIndex);
    }
  }, [focusWeekIndex]);

  if (isLoading) {
    return (
      <ChakraBox paddingTop="30px" width="100%">
        <SkeletonLoaderList
          direction="column"
          id={`${TOP_LEVEL_ID}__placeholder`}
          itemHeight="78px"
        />
      </ChakraBox>
    );
  }

  return (
    <ChakraFlex direction="column" gap="12px" width="100%" minHeight={0}>
      <Text id={`${TOP_LEVEL_ID}__header`} variant="h5" width="100%" flexShrink={0}>
        Appointments
      </Text>
      <ChakraBox
        flex="1 1 auto"
        minHeight={0}
        overflowY="auto"
        paddingRight="4px"
        onWheel={(e) => e.stopPropagation()}
        sx={{
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'neutral.line',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'neutral.secondary',
          },
        }}
      >
        <ChakraAccordion
          allowToggle
          index={openIndex}
          onChange={(idx: number) => setOpenIndex(idx === openIndex ? undefined : idx)}
        >
          {appointmentsByWeek.map((week, index) => (
            <ChakraAccordionItem
              key={`week-${index + 1}`}
              borderWidth={
                index === appointmentsByWeek.length - 1 ? '0' : '0 0 1px 0'
              }
              borderColor="neutral.line"
              borderRadius="0px"
            >
              <ChakraAccordionButton
                _hover={{ backgroundColor: 'neutral.background', borderRadius: '8px' }}
                padding="20px 8px"
              >
                <ChakraBox flex="1" textAlign="left">
                  <ChakraFlex alignItems="center" gap={2}>
                    <Text marginBottom="-3px" variant='bodyMedium'>Week {index + 1}</Text>
                    <Counter
                      id={`${TOP_LEVEL_ID}__week-${index + 1}-counter`}
                      number={week.appointments.length}
                    />
                  </ChakraFlex>
                </ChakraBox>
                <ChakraAccordionIcon />
              </ChakraAccordionButton>
              <ChakraAccordionPanel marginBottom="12px" padding="6px 0px 6px 0px">
                {week.appointments.length === 0 ? (
                  <Text color="neutral.secondary" size="sm">
                    No appointments scheduled
                  </Text>
                ) : (
                  <ChakraFlex direction="column" gap={3}>
                    {week.appointments.map((appointment) => (
                      <AppointmentCard
                        avatarProps={{
                          name: appointment.hostName,
                          src: appointment.hostAvatar || undefined,
                          userType: 'provider',
                        }}
                        badge={
                          appointment.isSelected ? (
                            <Tag
                              color={TagColor.pink}
                              id={`${TOP_LEVEL_ID}--${appointment.id}--selected-tag`}
                              size="sm"
                              value="Selected"
                            />
                          ) : undefined
                        }
                        endTimeISO={appointment.endTime}
                        formatDate={formatDate}
                        formatStartEndTime={formatStartEndTime}
                        id={`${TOP_LEVEL_ID}--${appointment.id}`}
                        key={appointment.id}
                        startTimeISO={appointment.startTime}
                        title={appointment.title}
                      />
                    ))}
                  </ChakraFlex>
                )}
              </ChakraAccordionPanel>
            </ChakraAccordionItem>
          ))}
        </ChakraAccordion>
      </ChakraBox>
    </ChakraFlex>
  );
};

export default AppointmentsPanel;
